import { computed } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useRoute } from "vue-router";
import { useApi } from "@shopexperts/shared";
import type {
  ParticipantDisplayMap,
  ProjectDetail,
  ProjectMediaResource,
} from "@/v2Projects/types";
import { useProjectContext } from "./useProjectContext";

export function useProjectDetail() {
  const route = useRoute();
  const { side } = useProjectContext();
  const api = useApi();
  const projectId = computed(() => Number(route.params.id));

  const detailQuery = useQuery({
    queryKey: computed(() => ["v2projects", "detail", projectId.value]),
    queryFn: async (): Promise<ProjectDetail> => {
      const projectsApi =
        side.value === "expert" ? api.expert.projectsV2 : api.client.projectsV2;
      return (await projectsApi.getProject({
        params: { projectId: projectId.value },
      })) as unknown as ProjectDetail;
    },
    enabled: computed(() => projectId.value > 0),
    staleTime: 10_000,
  });

  const participantsQuery = useQuery({
    queryKey: computed(() => ["v2projects", "participants", projectId.value]),
    queryFn: async (): Promise<ParticipantDisplayMap> => {
      const projectsApi =
        side.value === "expert" ? api.expert.projectsV2 : api.client.projectsV2;
      return (await projectsApi.getParticipantsDisplay({
        params: { projectId: projectId.value },
      })) as unknown as ParticipantDisplayMap;
    },
    enabled: computed(() => projectId.value > 0),
    staleTime: 60_000,
  });

  const attachmentsQuery = useQuery({
    queryKey: computed(() => ["v2projects", "attachments", projectId.value]),
    queryFn: async (): Promise<ProjectMediaResource[]> => {
      const projectsApi =
        side.value === "expert" ? api.expert.projectsV2 : api.client.projectsV2;
      return (await projectsApi.listAttachments({
        params: { projectId: projectId.value },
      })) as unknown as ProjectMediaResource[];
    },
    enabled: computed(() => projectId.value > 0),
    staleTime: 10_000,
  });

  return {
    project: computed(
      () => detailQuery.data.value ?? null,
    ),
    attachments: computed(
      () => attachmentsQuery.data.value ?? [],
    ),
    participants: computed(
      () => participantsQuery.data.value ?? null,
    ),
    projectId,
    side,
    isLoading: computed(() => detailQuery.isLoading.value),
    error: computed(() => detailQuery.error.value),
    refetch: detailQuery.refetch,
  };
}
